import {
  DEFAULT_MESSAGE_ATTACHMENT_LIMITS,
  IDEMPOTENCY_KEY_HEADER,
  MESSAGE_ATTACHMENTS_PART,
  MESSAGE_PART,
  type MessageAttachmentLimits,
  type PostedTicketMessage,
  type PostTicketMessage,
  type TicketMessageAttachment,
} from '@easyhelp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiFetch, apiRequest } from './client';
import { ticketsQueryKey } from './tickets';

/** Un messaggio da scrivere con la chiave di idempotenza della sua bozza. */
export interface TicketMessageDraft {
  message: PostTicketMessage;
  /** Una per bozza, riusata sui ritentativi: la stessa chiave non rispedisce l'email. */
  idempotencyKey: string;
  /** Allegati dell'operatore (#43), nell'ordine scelto; con almeno un file la richiesta è multipart. */
  files?: readonly File[];
}

/** Corpo multipart: la parte `message` con lo stesso JSON e una parte `attachments` per file. */
function multipartBody(message: PostTicketMessage, files: readonly File[]): FormData {
  const form = new FormData();
  form.append(MESSAGE_PART, JSON.stringify(message));
  for (const file of files) form.append(MESSAGE_ATTACHMENTS_PART, file, file.name);
  return form;
}

/** `POST /tickets/:code/messages`: risposta a chi ha segnalato o nota interna (#5), con allegati (#43). */
export function postTicketMessage(code: string, { message, idempotencyKey, files = [] }: TicketMessageDraft) {
  return apiRequest<PostedTicketMessage>(`/tickets/${encodeURIComponent(code)}/messages`, {
    method: 'POST',
    headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
    body: files.length > 0 ? multipartBody(message, files) : JSON.stringify(message),
  });
}

/**
 * Scrive nel thread del ticket. Dopo l'invio invalida tutte le chiavi dei ticket: il dettaglio
 * mostra il messaggio e lo stato nuovo, la coda lo stato e la ricerca il testo.
 */
export function usePostTicketMessage(code: string) {
  const queryClient = useQueryClient();
  return useMutation<PostedTicketMessage, ApiError, TicketMessageDraft>({
    mutationFn: (draft) => postTicketMessage(code, draft),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ticketsQueryKey }),
  });
}

/** Percorso del download di un allegato di risposta o nota (#43), sotto il prefisso dell'API. */
export function messageAttachmentPath(code: string, attachmentId: string): string {
  return `/tickets/${encodeURIComponent(code)}/messages/attachments/${encodeURIComponent(attachmentId)}`;
}

/** Quanto resta valido l'indirizzo `blob:` del file scaricato: il tempo che il browser lo salvi. */
const BLOB_URL_LIFETIME_MS = 60_000;

/**
 * Scarica un allegato di risposta o nota (#43) con il cookie di sessione: l'API registra l'accesso
 * e risponde `Content-Disposition: attachment`. Il file si salva solo se la risposta è 2xx: un 401 o
 * un 503 diventano `ApiError` (mai un JSON d'errore salvato come file). Il contenuto non è mai
 * mostrato nella pagina: passa da un indirizzo `blob:` a un link con `download`.
 */
export async function downloadMessageAttachment(code: string, attachment: TicketMessageAttachment): Promise<void> {
  // `apiFetch` (#8): cookie di sessione e `ApiError` per ogni risposta non 2xx.
  const response = await apiFetch(messageAttachmentPath(code, attachment.id));
  const href = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = href;
  link.download = attachment.filename;
  link.rel = 'noopener';
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(href), BLOB_URL_LIFETIME_MS);
}

/**
 * Download di un allegato come mutazione: un 401 azzera la sessione come ogni altra chiamata
 * (`createQueryClient`), gli altri errori restano nella mutazione per il messaggio.
 */
export function useDownloadMessageAttachment(code: string) {
  return useMutation<void, ApiError, TicketMessageAttachment>({
    mutationFn: (attachment) => downloadMessageAttachment(code, attachment),
  });
}

/**
 * Limiti degli allegati dell'istanza (`GET /message-attachments/limits`), per la verifica nel
 * composer; finché non arrivano, o se la richiesta fallisce, valgono i default di DEC-008. La verifica
 * che conta resta quella dell'API.
 */
export function useMessageAttachmentLimits(): MessageAttachmentLimits {
  const query = useQuery({
    queryKey: ['message-attachments', 'limits'],
    queryFn: () => apiRequest<MessageAttachmentLimits>('/message-attachments/limits'),
    staleTime: Infinity,
  });
  return query.data ?? DEFAULT_MESSAGE_ATTACHMENT_LIMITS;
}
